(function () {
  'use strict';

  class VariantMediaGallery {
    constructor(gallery) {
      this.gallery = gallery;
      this.sectionId = gallery.dataset.sectionId;
      this.productId = gallery.dataset.productId;

      this.slides = Array.from(
        gallery.querySelectorAll('[data-media-id]')
      ).filter((element) => {
        return element.closest('.product__media-list');
      });

      this.thumbnails = Array.from(
        gallery.querySelectorAll('.thumbnail-list__item[data-media-id]')
      );

      this.lastVariantId = gallery.dataset.selectedVariantId || '';

      this.init();
    }

    init() {
      this.bindVariantEvents();
      this.bindThumbnailEvents();

      /*
       * Run once on page load.
       */
      if (this.lastVariantId) {
        this.updateGallery(this.lastVariantId, false);
      }
    }

    /*
     * Listen for Dawn's variant events.
     *
     * Dawn publishes variant changes through the global pub/sub system.
     */
    bindVariantEvents() {
      document.addEventListener(
        'variant:change',
        (event) => {
          const variant = event.detail && event.detail.variant;

          if (!variant) {
            return;
          }

          this.handleVariantChange(variant);
        }
      );

      /*
       * Fallback for Dawn versions where the event payload
       * is attached differently.
       */
      document.addEventListener(
        'variantChange',
        (event) => {
          const variant =
            event.detail?.variant ||
            event.detail?.currentVariant ||
            event.detail;

          if (!variant || !variant.id) {
            return;
          }

          this.handleVariantChange(variant);
        }
      );

      /*
       * Watch the selected variant JSON used by Dawn.
       *
       * This makes the solution more tolerant of different
       * Dawn versions/customized variant pickers.
       */
      const variantJson = document.querySelector(
        '[data-selected-variant]'
      );

      if (variantJson) {
        const observer = new MutationObserver(() => {
          try {
            const variant = JSON.parse(
              variantJson.textContent.trim()
            );

            if (variant && variant.id) {
              this.handleVariantChange(variant);
            }
          } catch (error) {
            // Invalid JSON - ignore.
          }
        });

        observer.observe(variantJson, {
          childList: true,
          characterData: true,
          subtree: true
        });
      }

      /*
       * Also listen for option/select changes.
       * This catches customized Dawn variant pickers.
       */
      document.addEventListener('change', (event) => {
        const target = event.target;

        if (!target) {
          return;
        }

        if (
          target.matches(
            'variant-selects select, variant-radios input, .product-form__input input, .product-form__input select'
          )
        ) {
          setTimeout(() => {
            this.detectVariantFromProductForm();
          }, 50);
        }
      });
    }

    bindThumbnailEvents() {
      this.thumbnails.forEach((thumbnail) => {
        const button = thumbnail.querySelector('button');

        if (!button) {
          return;
        }

        button.addEventListener('click', () => {
          const mediaId = thumbnail.dataset.mediaId;

          if (!mediaId) {
            return;
          }

          this.activateMedia(mediaId);
        });
      });
    }

    handleVariantChange(variant) {
      if (!variant || !variant.id) {
        return;
      }

      const variantId = String(variant.id);

      /*
       * Prevent duplicate execution.
       */
      if (variantId === this.lastVariantId) {
        return;
      }

      this.lastVariantId = variantId;

      this.gallery.dataset.selectedVariantId = variantId;

      this.updateGallery(variantId, true, variant.featured_media?.id);
    }

    detectVariantFromProductForm() {
      /*
       * Dawn normally exposes the selected variant in JSON.
       */
      const selectedVariantElement = document.querySelector(
        '[data-selected-variant]'
      );

      if (!selectedVariantElement) {
        return;
      }

      try {
        const variant = JSON.parse(
          selectedVariantElement.textContent.trim()
        );

        if (variant && variant.id) {
          this.handleVariantChange(variant);
        }
      } catch (error) {
        // Ignore invalid JSON.
      }
    }

    updateGallery(variantId, animate = true, featuredMediaId = null) {
      const id = String(variantId);

      let firstVisibleMedia = null;
      let visibleCount = 0;

      this.slides.forEach((slide) => {
        const mediaId = String(slide.dataset.mediaId || '');
        const slideVariantId = String(
          slide.dataset.variantId || ''
        );

        const isVariantMedia =
          slide.dataset.variantMedia === 'true';

        let shouldShow = true;

        /*
         * Variant-specific media:
         * only show media belonging to the selected variant.
         */
        if (isVariantMedia) {
          shouldShow = slideVariantId === id;
        }

        /*
         * Always show the selected variant's featured media.
         */
        if (
          featuredMediaId &&
          String(featuredMediaId) === mediaId
        ) {
          shouldShow = true;
        }

        this.setVisibility(slide, shouldShow);

        if (shouldShow) {
          visibleCount++;

          if (!firstVisibleMedia) {
            firstVisibleMedia = slide;
          }
        }
      });

      /*
       * Update thumbnails.
       */
      this.thumbnails.forEach((thumbnail) => {
        const thumbnailVariantId = String(
          thumbnail.dataset.variantId || ''
        );

        const isVariantMedia =
          thumbnail.dataset.variantMedia === 'true';

        let shouldShow = true;

        if (isVariantMedia) {
          shouldShow = thumbnailVariantId === id;
        }

        this.setVisibility(thumbnail, shouldShow);

        const button = thumbnail.querySelector('button');

        if (button) {
          button.setAttribute(
            'aria-current',
            'false'
          );

          button.classList.remove('is-active');
        }
      });

      /*
       * If no media was assigned to this variant,
       * keep the general product media visible.
       */
      if (visibleCount === 0) {
        this.slides.forEach((slide) => {
          this.setVisibility(slide, true);
        });

        this.thumbnails.forEach((thumbnail) => {
          this.setVisibility(thumbnail, true);
        });

        firstVisibleMedia = this.slides[0] || null;
      }

      /*
       * Select the featured image first.
       */
      if (featuredMediaId) {
        const featuredSlide = this.slides.find(
          (slide) =>
            String(slide.dataset.mediaId) ===
            String(featuredMediaId)
        );

        if (
          featuredSlide &&
          !featuredSlide.hidden
        ) {
          firstVisibleMedia = featuredSlide;
        }
      }

      if (firstVisibleMedia) {
        this.activateMedia(
          firstVisibleMedia.dataset.mediaId,
          animate
        );
      }

      this.refreshSlider();
    }

    setVisibility(element, visible) {
      if (!element) {
        return;
      }

      element.hidden = !visible;

      if (visible) {
        element.removeAttribute('aria-hidden');
      } else {
        element.setAttribute(
          'aria-hidden',
          'true'
        );
      }
    }

    activateMedia(mediaId, animate = true) {
      const id = String(mediaId);

      const targetSlide = this.slides.find(
        (slide) =>
          String(slide.dataset.mediaId) === id &&
          !slide.hidden
      );

      if (!targetSlide) {
        return;
      }

      /*
       * Remove active state from all slides.
       */
      this.slides.forEach((slide) => {
        slide.classList.remove('is-active');
      });

      targetSlide.classList.add('is-active');

      /*
       * Update thumbnail state.
       */
      this.thumbnails.forEach((thumbnail) => {
        const button =
          thumbnail.querySelector('button');

        const active =
          String(thumbnail.dataset.mediaId) === id &&
          !thumbnail.hidden;

        if (button) {
          button.classList.toggle(
            'is-active',
            active
          );

          button.setAttribute(
            'aria-current',
            active ? 'true' : 'false'
          );
        }
      });

      /*
       * Scroll the main gallery to the selected image.
       */
      const galleryList =
        this.gallery.querySelector(
          '.product__media-list'
        );

      if (galleryList) {
        const slideIndex =
          Array.from(galleryList.children)
            .filter((slide) => !slide.hidden)
            .indexOf(targetSlide);

        if (
          slideIndex >= 0 &&
          typeof galleryList.scrollTo === 'function'
        ) {
          const scrollLeft =
            targetSlide.offsetLeft;

          galleryList.scrollTo({
            left: scrollLeft,
            behavior: animate ? 'smooth' : 'auto'
          });
        }
      }

      /*
       * Scroll thumbnail into view.
       */
      const thumbnail =
        this.thumbnails.find(
          (item) =>
            String(item.dataset.mediaId) === id &&
            !item.hidden
        );

      if (
        thumbnail &&
        typeof thumbnail.scrollIntoView === 'function'
      ) {
        thumbnail.scrollIntoView({
          behavior: animate ? 'smooth' : 'auto',
          block: 'nearest',
          inline: 'nearest'
        });
      }

      /*
       * Update accessibility status.
       */
      const status =
        this.gallery.querySelector(
          '[role="status"]'
        );

      if (status) {
        status.textContent =
          'Product image updated.';
      }
    }

    refreshSlider() {
      /*
       * Dawn's slider component caches its slides.
       * Trigger resize so the native Dawn slider recalculates
       * visible items after variant filtering.
       */
      window.dispatchEvent(
        new Event('resize')
      );

      /*
       * Give Dawn's slider a moment to recalculate.
       */
      setTimeout(() => {
        window.dispatchEvent(
          new Event('resize')
        );
      }, 100);
    }
  }

  function initializeVariantGalleries() {
    const galleries = document.querySelectorAll(
      '[data-variant-gallery="true"]'
    );

    galleries.forEach((gallery) => {
      if (gallery.dataset.variantGalleryInitialized) {
        return;
      }

      gallery.dataset.variantGalleryInitialized =
        'true';

      new VariantMediaGallery(gallery);
    });
  }

  /*
   * Initial page load.
   */
  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initializeVariantGalleries
    );
  } else {
    initializeVariantGalleries();
  }

  /*
   * Shopify theme editor / dynamically loaded sections.
   */
  document.addEventListener(
    'shopify:section:load',
    initializeVariantGalleries
  );

})();